/**
 * Image cache hook for converting React SVG logos to PNG ArrayBuffers for PDF generation
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { fundLogos } from '../components/fund-logos';

// Convert React SVG component to PNG ArrayBuffer
async function svgComponentToPng(Component: React.ComponentType<{ className?: string }>): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    try {
      // Create a container element
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.width = '50px';
      container.style.height = '50px';
      document.body.appendChild(container);

      // Render the SVG component to get the SVG element
      const root = ReactDOM.createRoot(container);
      
      root.render(React.createElement(Component, { className: 'w-full h-full' }));

      // Wait for render
      setTimeout(() => {
        const svgElement = container.querySelector('svg');
        
        if (!svgElement) {
          document.body.removeChild(container);
          reject(new Error('SVG element not found'));
          return;
        }

        // Force white color on all paths
        const paths = svgElement.querySelectorAll('path, circle, rect, polygon');
        paths.forEach((path) => {
          (path as SVGElement).setAttribute('fill', 'white');
          (path as SVGElement).setAttribute('stroke', 'white');
        });
        
        // Get SVG markup
        const serializer = new XMLSerializer();
        let svgMarkup = serializer.serializeToString(svgElement);
        
        // Ensure proper SVG attributes
        if (!svgMarkup.includes('xmlns')) {
          svgMarkup = svgMarkup.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
        }
        
        console.log(`Converting logo, SVG markup length: ${svgMarkup.length}`);
        
        // Create canvas
        const canvas = document.createElement('canvas');
        const size = 160; // High resolution for PDF
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d')!;

        // Create image from SVG
        const img = new Image();
        const svgBlob = new Blob([svgMarkup], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);

        img.onload = () => {
          // Draw circular background
          ctx.beginPath();
          ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI);
          ctx.fillStyle = '#90d977';
          ctx.fill();

          // Draw SVG on canvas with padding (centered)
          const padding = 40; // More padding for better visibility
          ctx.drawImage(img, padding, padding, size - padding * 2, size - padding * 2);

          console.log('Canvas drawn, converting to PNG...');

          // Convert canvas to PNG
          canvas.toBlob((blob) => {
            if (blob) {
              blob.arrayBuffer().then((buffer) => {
                console.log(`PNG created, size: ${buffer.byteLength} bytes`);
                URL.revokeObjectURL(url);
                root.unmount();
                document.body.removeChild(container);
                resolve(buffer);
              });
            } else {
              URL.revokeObjectURL(url);
              root.unmount();
              document.body.removeChild(container);
              reject(new Error('Failed to convert canvas to blob'));
            }
          }, 'image/png');
        };

        img.onerror = (error) => {
          console.error('Image load error:', error);
          URL.revokeObjectURL(url);
          root.unmount();
          document.body.removeChild(container);
          reject(new Error('Failed to load SVG image'));
        };

        img.src = url;
      }, 300); // Increased timeout to ensure render completes
    } catch (error) {
      console.error('SVG conversion error:', error);
      reject(error);
    }
  });
}

export function useImageCache() {
  return { 
    isLoading: false, 
    cacheReady: true, 
    getImage: async (_url: string) => new ArrayBuffer(0), 
    getImageSync: (_url: string) => null
  };
}

export async function getImage(_url: string): Promise<ArrayBuffer> {
  return new ArrayBuffer(0);
}

export function getImageSync(_url: string): ArrayBuffer | null {
  return null;
}

// Pre-load fund logos as PNG ArrayBuffers for PDF generation
export async function getRequiredFundLogos(fundNames: string[]): Promise<Map<string, ArrayBuffer>> {
  const logoMap = new Map<string, ArrayBuffer>();
  
  // Process logos sequentially to avoid overwhelming the browser
  for (const name of fundNames) {
    const LogoComponent = fundLogos[name];
    if (LogoComponent) {
      try {
        const buffer = await svgComponentToPng(LogoComponent);
        logoMap.set(name, buffer);
      } catch (error) {
        console.error(`Failed to convert logo for ${name}:`, error);
        // Don't reject, just skip this logo
      }
    }
  }

  return logoMap;
}